import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createEntityAddress, deleteEntityAddress, getEntityAddresses, setEntityPrimaryAddress, updateEntityAddress } from "../../api/entities/Address";
import { AddressForm } from "../../types/forms/entity";

export const useEntityAddress = (entityIdStr?: string) => {
  const queryClient = useQueryClient();
  const entityId = Number(entityIdStr);

  const queryKey = ["entity", entityId, "address"];

  const result = useQuery({
    queryKey,
    queryFn: () => getEntityAddresses(entityId),
    enabled: !!entityId,
  });

  const createMutation = useMutation({
    mutationFn: (values: AddressForm) => createEntityAddress(entityId, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ addressId, values }: { addressId: number, values: AddressForm }) => updateEntityAddress(entityId, addressId, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (addressId: number) => deleteEntityAddress(entityId, addressId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  const setPrimaryMutation = useMutation({
    mutationFn: (addressId: number) => setEntityPrimaryAddress(entityId, addressId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return {
    addressesData: result.data ?? [],
    isPending: result.isPending,
    isError: result.isError,
    error: result.error,
    createAddress: createMutation.mutateAsync,
    updateAddress: updateMutation.mutateAsync,
    deleteAddress: deleteMutation.mutateAsync,
    setPrimaryAddress: setPrimaryMutation.mutateAsync,
    isUpdating: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending || setPrimaryMutation.isPending,
  }
}